import React, { useState, useCallback, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { MainContent } from './components/MainContent';
import { ErrorBoundary } from './components/ErrorBoundary';
import { JsonFetcher } from './services/jsonFetcher';
import { SchemaDetector } from './services/schemaDetector';
import { ExportService } from './services/exportService';
import { activityLogger } from './services/activityLogger';
import { JsonData, UrlHistory as UrlHistoryType, ExportOptions } from './types';
import axios from 'axios';

const App: React.FC = () => {
  const [activeSection, setActiveSection] = useState('command');
  const [currentData, setCurrentData] = useState<JsonData | null>(null);
  const [filteredData, setFilteredData] = useState<JsonData | null>(null);
  const [statistics, setStatistics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [history, setHistory] = useState<UrlHistoryType[]>([]);
  const [scrapingJob, setScrapingJob] = useState<{ id: string, status: string } | null>(null);

  // Load history from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('json-dashboard-history');
    if (stored) {
      try {
        const parsed = JSON.parse(stored).map((item: any) => ({
          ...item,
          timestamp: new Date(item.timestamp)
        }));
        setHistory(parsed);
      } catch (error) {
        console.error('Failed to load history:', error);
      }
    }
  }, []);

  // Save history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('json-dashboard-history', JSON.stringify(history));
  }, [history]);

  const handleUrlSubmit = useCallback(async (url: string) => {
    const startTime = Date.now();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      activityLogger.logDataOperation('fetch_start', url);

      const fetcher = JsonFetcher.getInstance();
      const data = await fetcher.fetchJson(url);

      const schemaData = SchemaDetector.detectSchema(data);
      const stats = SchemaDetector.generateStatistics(data);

      setCurrentData(data);
      setFilteredData(data);
      setStatistics(stats);
      setSuccess(true);

      // Add to history
      const historyItem: UrlHistoryType = {
        id: Date.now().toString(),
        url,
        timestamp: new Date(),
        data,
        schema: schemaData,
        status: 'success'
      };

      setHistory(prev => [historyItem, ...prev.slice(0, 49)]); // Keep last 50 items

      activityLogger.logDataOperation('fetch_success', url, {
        duration: Date.now() - startTime,
        dataSize: JSON.stringify(data).length,
        schemaType: schemaData.type
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
      setError(errorMessage);

      // Add failed attempt to history
      const historyItem: UrlHistoryType = {
        id: Date.now().toString(),
        url,
        timestamp: new Date(),
        data: null as any,
        status: 'error',
        error: errorMessage
      };

      setHistory(prev => [historyItem, ...prev.slice(0, 49)]);

      activityLogger.logError(err instanceof Error ? err : new Error(errorMessage), { url });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleScrape = useCallback(async (platforms: string[], query: string) => {
    const startTime = Date.now();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      activityLogger.logDataOperation('scrape_start', query);

      const response = await axios.post('https://axa-scraping-agent.sv9.workers.dev/api/scrape', {
        platforms,
        query,
        options: {}
      }, {
        headers: { 'X-API-Key': 'your_secure_api_key_here' }
      });

      const jobId = response.data.jobId;
      setScrapingJob({ id: jobId, status: 'running' });

      // Poll for status with exponential backoff
      let pollInterval = 2000;
      const pollStatus = async () => {
        try {
          const statusRes = await axios.get(`https://axa-scraping-agent.sv9.workers.dev/api/status?jobId=${jobId}`, {
            headers: { 'X-API-Key': 'your_secure_api_key_here' }
          });
          const status = statusRes.data.status;

          if (status === 'completed') {
            const resultsRes = await axios.get(`https://axa-scraping-agent.sv9.workers.dev/api/results/${jobId}`, {
              headers: { 'X-API-Key': 'your_secure_api_key_here' }
            });
            const data = resultsRes.data;

            // Process as JSON
            const schemaData = SchemaDetector.detectSchema(data);
            const stats = SchemaDetector.generateStatistics(data);

            setCurrentData(data);
            setFilteredData(data);
            setStatistics(stats);
            setSuccess(true);
            setScrapingJob(null);

            // Add to history
            const historyItem: UrlHistoryType = {
              id: Date.now().toString(),
              url: `Scraped: ${query} from ${platforms.join(', ')}`,
              timestamp: new Date(),
              data,
              schema: schemaData,
              status: 'success'
            };

            setHistory(prev => [historyItem, ...prev.slice(0, 49)]);

            activityLogger.logDataOperation('scrape_success', query, {
              duration: Date.now() - startTime,
              dataSize: JSON.stringify(data).length,
              platforms
            });

          } else if (status === 'failed') {
            setError('Scraping job failed');
            setScrapingJob(null);
          } else {
            // Exponential backoff: increase interval up to 10 seconds
            pollInterval = Math.min(pollInterval * 1.2, 10000);
            setTimeout(pollStatus, pollInterval);
          }
        } catch (err) {
          setError('Failed to check scraping status');
          setScrapingJob(null);
        } finally {
          setIsLoading(false);
        }
      };

      pollStatus();

    } catch (err) {
      setError('Failed to start scraping');
      setIsLoading(false);
      activityLogger.logError(err instanceof Error ? err : new Error('Scrape failed'), { query, platforms });
    }
  }, []);

  const handleFilterChange = useCallback((filtered: JsonData) => {
    setFilteredData(filtered);
  }, []);

  const handleExport = useCallback(async (format: ExportOptions['format']) => {
    if (!currentData) return;

    try {
      await ExportService.exportData(currentData, {
        format,
        includeSchema: true,
        includeMetadata: true
      });

      activityLogger.logInteraction('export', 'export-button', { format });
    } catch (err) {
      setError(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      activityLogger.logError(err instanceof Error ? err : new Error('Export failed'), { format });
    }
  }, [currentData]);

  const handleLoadHistoryUrl = useCallback((url: string) => {
    handleUrlSubmit(url);
    activityLogger.logInteraction('load_history', 'history-item', { url });
  }, [handleUrlSubmit]);

  const handleClearHistory = useCallback(() => {
    setHistory([]);
    activityLogger.logInteraction('clear_history', 'clear-button');
  }, []);

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar
        activeSection={activeSection}
        onSectionChange={setActiveSection}
      />

      {/* Main Content */}
      <ErrorBoundary>
        <MainContent
          activeSection={activeSection}
          jsonData={currentData}
          filteredData={filteredData}
          statistics={statistics}
          isLoading={isLoading}
          error={error}
          success={success}
          history={history}
          onUrlSubmit={handleUrlSubmit}
          onFilterChange={handleFilterChange}
          onExport={handleExport}
          onLoadHistoryUrl={handleLoadHistoryUrl}
          onClearHistory={handleClearHistory}
          onScrape={handleScrape}
          scrapingJob={scrapingJob}
          onSectionChange={setActiveSection}
        />
      </ErrorBoundary>
    </div>
  );
};

export default App;
